import { useEffect, useState } from 'react'
import { FiX } from 'react-icons/fi'

interface PdfViewerModalProps {
    isOpen: boolean
    onClose: () => void
    pdfUrl: string | null
    title?: string
    /** When true, show "Loading document..." (e.g. while parent fetches PDF). */
    isLoading?: boolean
    /** If provided, show an "Open in Xero" button that opens this URL in a new tab. */
    openInXeroUrl?: string | null
    /** When PDF failed to load, show this message instead of generic "No document to display". */
    errorMessage?: string | null
}

const PdfViewerModal = ({ isOpen, onClose, pdfUrl, title = 'Document Viewer', isLoading: externalLoading = false, openInXeroUrl, errorMessage }: PdfViewerModalProps) => {
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (isOpen && pdfUrl) {
            setLoading(true)
        }
    }, [isOpen, pdfUrl])

    const showLoading = externalLoading || (!!pdfUrl && loading)

    if (!isOpen) return null

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
            onClick={onClose}
        >
            <div
                className="relative bg-white rounded-lg shadow-xl"
                style={{
                    width: '90vw',
                    height: '90vh',
                    maxWidth: '1200px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-6 py-4 border-b"
                    style={{
                        borderColor: '#E9E9EA',
                    }}
                >
                    <h2
                        style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 600,
                            fontSize: '18px',
                            lineHeight: '28px',
                            color: '#212126',
                        }}
                    >
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        style={{
                            color: '#69697A',
                        }}
                    >
                        <FiX size={20} />
                    </button>
                </div>

                {/* PDF Viewer */}
                <div
                    className="flex-1 relative"
                    style={{
                        overflow: 'hidden',
                        backgroundColor: '#525659',
                    }}
                >
                    {showLoading && (
                        <div
                            className="absolute inset-0 flex items-center justify-center"
                            style={{
                                backgroundColor: '#F9FAFB',
                                zIndex: 10,
                            }}
                        >
                            <div
                                style={{
                                    fontFamily: 'Lexend Deca',
                                    fontSize: '14px',
                                    color: '#69697A',
                                }}
                            >
                                Loading document...
                            </div>
                        </div>
                    )}
                    {pdfUrl ? (
                        <object
                            data={pdfUrl}
                            type="application/pdf"
                            className="w-full h-full"
                            style={{
                                border: 'none',
                            }}
                            onLoad={() => setLoading(false)}
                        >
                            <div
                                className="absolute inset-0 flex flex-col items-center justify-center gap-4"
                                style={{
                                    backgroundColor: '#F9FAFB',
                                    padding: '2rem',
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: 'Lexend Deca',
                                        fontSize: '14px',
                                        color: '#69697A',
                                        textAlign: 'center',
                                    }}
                                >
                                    PDF cannot be displayed in this browser.
                                </div>
                                <a
                                    href={pdfUrl}
                                    download
                                    className="px-4 py-2 rounded-lg transition-opacity hover:opacity-90"
                                    style={{
                                        fontFamily: 'Lexend Deca',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        color: '#FFFFFF',
                                        backgroundColor: '#212126',
                                        textDecoration: 'none',
                                    }}
                                >
                                    Download PDF Instead
                                </a>
                            </div>
                        </object>
                    ) : (
                        <div
                            className="absolute inset-0 flex flex-col items-center justify-center gap-4"
                            style={{
                                backgroundColor: '#F9FAFB',
                                padding: '2rem',
                            }}
                        >
                            <div
                                style={{
                                    fontFamily: 'Lexend Deca',
                                    fontSize: '14px',
                                    color: '#69697A',
                                    textAlign: 'center',
                                    maxWidth: '400px',
                                }}
                            >
                                {errorMessage || 'No document to display'}
                            </div>
                            {openInXeroUrl && (
                                <a
                                    href={openInXeroUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 rounded-lg transition-opacity hover:opacity-90"
                                    style={{
                                        fontFamily: 'Lexend Deca',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        color: '#FFFFFF',
                                        backgroundColor: '#212126',
                                        textDecoration: 'none',
                                    }}
                                >
                                    Open in Xero
                                </a>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer with actions */}
                <div
                    className="flex items-center justify-end gap-3 px-6 py-4 border-t"
                    style={{
                        borderColor: '#E9E9EA',
                    }}
                >
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                        style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 500,
                            fontSize: '14px',
                            color: '#212126',
                            border: '1px solid #E9E9EA',
                        }}
                    >
                        Close
                    </button>
                    {openInXeroUrl && (
                        <a
                            href={openInXeroUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 rounded-lg transition-opacity hover:opacity-90"
                            style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#212126',
                                border: '1px solid #E9E9EA',
                                textDecoration: 'none',
                            }}
                        >
                            Open in Xero
                        </a>
                    )}
                    {pdfUrl && (
                        <a
                            href={pdfUrl}
                            download
                            className="px-4 py-2 rounded-lg transition-opacity hover:opacity-90"
                            style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#FFFFFF',
                                backgroundColor: '#212126',
                            }}
                        >
                            Download
                        </a>
                    )}
                </div>
            </div>
        </div>
    )
}

export default PdfViewerModal
